// StudioFind — "Find on this desk" (pen "Studio – find on the desk"): a pill
// at the top centre of the canvas (or F); open, a field with the match count,
// the filter chips and the matches, each with where it lies. ↑ ↓ move, Enter
// or a click picks one (the canvas flies there and selects it), Esc closes.
// The canvas lights the matches up in place (studioFind.ts). Pure but for
// view-ephemeral state (the highlighted row); the text and filter are the
// canvas's, every change goes out as an intent.

export { StudioFind };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type KeyboardEvent, type ReactNode, useState } from "react";
import { Icon, type IconName } from "../Icon/Icon.js";
import { Kbd } from "../Kbd/Kbd.js";
import {
    FIND_FILTERS,
    type FindFilter,
    type FindItem,
} from "../Studio/studioFind.js";
import "./StudioFind.css";

// More than this many matches: the list shows the first ones; the canvas
// still lights up every match.
const LISTED = 8;

const iconOf = (kind: FindItem["kind"]): IconName => {
    switch (kind) {
        case "source":
            return "source";
        case "material":
            return "material";
        case "module":
            return "module";
        case "course":
            return "course";
        case "other":
            return "file";
        default:
            return exhaustSwitchCase(kind);
    }
};

const StudioFind = ({
    open,
    text,
    filter,
    hits,
    onOpen,
    onClose,
    onText,
    onFilter,
    onPick,
}: {
    readonly open: boolean;
    readonly text: string;
    readonly filter: FindFilter;
    readonly hits: readonly FindItem[];
    readonly onOpen: () => void;
    readonly onClose: () => void;
    readonly onText: (text: string) => void;
    readonly onFilter: (filter: FindFilter) => void;
    readonly onPick: (hit: FindItem) => void;
}): ReactNode => {
    const { t } = useLingui();
    const [active, setActive] = useState(0);
    if (!open)
        return (
            <button type="button" className="bilda-find__pill" onClick={onOpen}>
                <Icon name="search" size="sm" />
                {t`Find on this desk`}
                <Kbd className="bilda-find__key" keys={["F"]} />
            </button>
        );
    const label = (f: FindFilter): string => {
        switch (f) {
            case "all":
                return t`All`;
            case "sources":
                return t`Sources`;
            case "materials":
                return t`Materials`;
            case "modules":
                return t`Modules`;
            case "empty":
                return t`Empty`;
            case "planned":
                return t`Planned`;
            case "ai":
                return t`Made by AI`;
            default:
                return exhaustSwitchCase(f);
        }
    };
    const listed = hits.slice(0, LISTED);
    const at = Math.min(active, Math.max(0, listed.length - 1));
    const looking = text.trim() !== "" || filter !== "all";
    const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
        // The canvas's keys never act on what is typed here.
        e.stopPropagation();
        switch (e.key) {
            case "Escape":
                e.preventDefault();
                onClose();
                return;
            case "ArrowDown":
            case "ArrowUp": {
                e.preventDefault();
                const step = e.key === "ArrowDown" ? 1 : -1;
                const n = listed.length;
                if (n > 0) setActive((at + step + n) % n);
                return;
            }
            case "Enter": {
                const hit = listed[at];
                if (hit !== undefined) {
                    e.preventDefault();
                    onPick(hit);
                }
                return;
            }
            default:
                return;
        }
    };
    return (
        <search className="bilda-find" aria-label={t`Find on this desk`}>
            <label className="bilda-find__field">
                <Icon name="search" size="sm" />
                <input
                    // biome-ignore lint/a11y/noAutofocus: opening find is asking to type.
                    autoFocus
                    type="search"
                    value={text}
                    placeholder={t`Find on this desk …`}
                    aria-label={t`Find on this desk`}
                    onChange={(e) => {
                        setActive(0);
                        onText(e.target.value);
                    }}
                    onKeyDown={onKey}
                />
                {looking ? (
                    <span className="bilda-find__count" aria-live="polite">
                        {t`${plural(hits.length, {
                            one: "# match",
                            other: "# matches",
                        })}`}
                    </span>
                ) : null}
                <Kbd className="bilda-find__key" keys={["Esc"]} />
            </label>
            <div className="bilda-find__chips">
                {FIND_FILTERS.map((f) => (
                    <button
                        key={f}
                        type="button"
                        className="bilda-find__chip"
                        aria-pressed={f === filter}
                        onClick={() => {
                            setActive(0);
                            onFilter(f);
                        }}
                    >
                        {label(f)}
                    </button>
                ))}
            </div>
            {!looking ? null : listed.length === 0 ? (
                <p className="bilda-find__none">
                    {t`Nothing on this desk matches “${text}”`}
                    <span>{t`Search everywhere (Cmd+K)`}</span>
                </p>
            ) : (
                <ul className="bilda-find__list">
                    {listed.map((hit, i) => {
                        const where = hit.inside.at(-1);
                        return (
                            <li key={`${hit.card}/${hit.id}`}>
                                <button
                                    type="button"
                                    className="bilda-find__row"
                                    aria-current={i === at ? "true" : undefined}
                                    onPointerEnter={() => setActive(i)}
                                    onClick={() => onPick(hit)}
                                >
                                    <Icon name={iconOf(hit.kind)} size="sm" />
                                    <span className="bilda-find__title">
                                        {hit.title}
                                    </span>
                                    <span className="bilda-find__where">
                                        {where === undefined
                                            ? t`On the desk`
                                            : t`Inside ${where.title}`}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </search>
    );
};
